import { Injectable } from '@angular/core';
import {
  FormGroup,
  AbstractControl,
  FormControl,
  Validators,
  ValidatorFn,
  ValidationErrors
} from '@angular/forms';
import {
  FieldConfig,
  FieldGroup,
  InterfaceSettings,
  AdaptOperation,
  InterfaceDisplayType,
  MasterData,
  I18nDescribable,
  IEvaluableExecutor
} from '../../api/contract-notice-dto';
import {
  ValidationResultDTO,
  ValidationExecutionDTO
} from '../../api/plyca-dtos-v2';
import * as objectPath from 'object-path';
import {
  FormGroupConfig, FunctionDef
} from './form.objects';
import { formElementComponent } from './formElement.component';
import { TranslateService } from '@ngx-translate/core';
import * as lodash from 'lodash';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { PaginationDTO } from '../../api/PaginationModel';

declare var jQuery: any;
const VALIDATORFN_REGEX = /^(.*)\((.*)\)$/;
// expresion regular para path asociados tablas
const TABLEPATH_REGEX = /^(.+)\[(\d+)\]\.?(.*)/;

@Injectable()
export class FormService {

  private objectConstructor = ({}).constructor;
  private evalDescriptionCache: Map<string, string> = new Map();

  constructor(
    private translationService: TranslateService// ,
    // private plycaService: PlycaDataService
  ) { }

  public getDefaultValue(model: any, field: FieldConfig, formElement: formElementComponent): any {
    let value = model ? this.getNestedProperty(model, field.jsonField) : null;
    let defaultValue;
    // Si el campo tiene valor por defecto lo calculamos
    if (formElement && formElement != null && field.defaultValue) {
      // Comprobamos si es un objeto o un script a evaluar
      if (field.defaultValue.constructor === this.objectConstructor) {
        if (field.type === 'SEARCH' && field.defaultValue.identifier
          && !field.defaultValue.description
          && field.defaultValue.identifier.includes('<>')) {
          let idParts = field.defaultValue.identifier.split('<>');
          formElement.plycaService.getMasterDataValues(idParts[1], idParts[0], true)
            .pipe(take(1))
            .subscribe((data: PaginationDTO<MasterData>) => {
              if (data && data.content && data.content.length > 0) {
                defaultValue = data.content[0];
                if ((value === undefined || value == null || value === '') && defaultValue !== undefined) {
                  if (model) {
                    this.setNestedProperty(model, field.jsonField, defaultValue);
                  }
                }
              }
            });
        } else {
          defaultValue = field.defaultValue;
        }
      } else {
        try {
          defaultValue = formElement.evalExpression(field.defaultValue);
        } catch (ex) {
          console.log('****ERROR calculando valor por defecto (jsonField:' + field.jsonField + ')=' + field.defaultValue, ex);
        }
      }
    }
    // Si el modelo no tiene valor para el campo y existe valor por defecto lo aplicamos
    if ((value === undefined || value == null) && defaultValue !== undefined) {
      value = defaultValue;
      if (model) {
        this.setNestedProperty(model, field.jsonField, value);
      }
    } else if (value == null) {
      value = '';
    }
    return value;
  }

  public createFormControl(model: any, field: FieldConfig, formElement: formElementComponent): FormControl {
    let value = this.getDefaultValue(model, field, formElement);
    let validators = new Array<ValidatorFn>();
    if (field.required && field.required.toString() === 'true') {
      validators.push(Validators.required);
    }
    if (field.maxlength) {
      validators.push(Validators.maxLength(field.maxlength));
    }
    if (field.minlength) {
      validators.push(Validators.minLength(field.minlength));
    }
    if (field.validators && field.validators.length > 0) {
      // la expresion puede ser un array de validadores https://angular.io/api/forms/Validators .
      //      por ejemplo: ["email","required","maxLength(20)",,"minLength(8),"pattern('^[a-zA-Z0-9_.+-]+')'"]
      field.validators.forEach(validatorName => {
        if (this.isValidatorFn(validatorName)) {
          let validation = Validators[this.getValidationFnName(validatorName)](this.getValidationFnParams(validatorName));
          validators.push(validation);
        } else {
          validators.push(Validators[validatorName]);
        }
      })
    }
    let control = new FormControl(value || '', validators);
    return control;
  }


  private isValidatorFn(validator: string): boolean {
    return RegExp(VALIDATORFN_REGEX).test(validator);
  }

  private getValidationFnParams(validator: string): string {
    let name: string;
    return validator.match(VALIDATORFN_REGEX)[2];
  }

  private getValidationFnName(validator: string): string {
    let name: string;
    return validator.match(VALIDATORFN_REGEX)[1];
  }

  public getFormControlKey(field: FieldConfig, rowUuid?: string): string {
    let id = field.id || field.jsonField;
    return this.formatControlId(id, rowUuid);
  }

  public getFormGroupId(groupId: string) {
    return 'group_' + this.formatControlId(groupId);
  }

  public formatControlId(id: string, rowUuid?: string): string {
    return id
      ? this.patchJsonPath(id) + (rowUuid ? '_' + rowUuid : '')
      : undefined;
  }

  private patchJsonPath(path: string): string {
    // reemplaza . por _ y [ o ] por -
    return path ? path.replace(/\./g, '_--').replace(/(\[|\])/g, '-') : undefined;
  }

  /**
   * Elimina errores y marca como modificados los campos de un formulario.
   * Debe usarse este método al invocar a un servicio de validación/envio
   * para que se marquen los errores
   */
  public deleteModelErrors(formGroupConfig: FormGroupConfig) {
    // limpiar errores almacenados en el modelo de cada registro de una tabla
    formGroupConfig.group.fieldList
      .filter((fieldConfig: FieldConfig) => fieldConfig.type === 'TABLE')
      .forEach((fieldConfig: FieldConfig) => {
        let model = this.getNestedProperty(
          formGroupConfig.model,
          fieldConfig.jsonField
        ) as any[];
        if (model) {
          model.forEach((row: any) => delete row.errors);
        }
      });
  }

  public markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls)
      .map(key => formGroup.controls[key])
      .filter(value => value instanceof AbstractControl)
      .forEach((control: AbstractControl) => {
        control.markAsTouched();
        this.clearFormControlError(control);
        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      });
  }

  public clearFormControlError(control: AbstractControl): void {
    control.setErrors(undefined);
    if (control.hasValidator(control['tmpServerValidators'])) {
      control.removeValidators(control['tmpServerValidators']);
    }
  }

  public getNestedProperty(object, property) {
    let ret;
    if (object && typeof object === 'object') {
      if (typeof property === 'string' && property !== '') {
        // Se modifican las posiciones de arrays numericas
        let modifiedProperty = property.replace(/\[(\d+)\]/g, '.$1')
        let split = modifiedProperty.split('.');
        ret = split.reduce(function (obj, prop) {
          return obj && obj[prop];
        }, object);
      } else if (typeof property === 'number') {
        ret = object[property];
      } else {
        ret = object;
      }
    } else {
      ret = object;
    }
    //    console.log('getNestedProperty', property, ret);
    return ret;
  }

  public setNestedProperty(obj, path, value) {
    let modifiedPath = path.replace(/\[(\d+)\]/g, '.$1')
    const pList = modifiedPath.split('.');
    const key = pList.pop();
    const pointer = pList.reduce((accumulator, currentValue) => {
      if (accumulator[currentValue] === undefined || accumulator[currentValue] == null) {
        accumulator[currentValue] = {};
      }
      return accumulator[currentValue];
    }, obj);
    pointer[key] = value;
    return obj;
  }

  public manageFormException(
    error: any,
    fieldGroups: FieldGroup | FieldGroup[],
    formGroup: FormGroup,
    model: any
  ): boolean {


    // los errores de validacionse lanzan con un HTTP 400 (Bad Request)
    // let errorObj = error.json ? error.json() : undefined;
    let errorObj = error;
    let fieldGroupList =
      fieldGroups instanceof Array ? fieldGroups : new Array(fieldGroups);
    // a este metodo se puede llegar como resultado de un error HttpErrorResponse,
    // o como resultado de una peticion HTTP de validacion  ValidationResultDTO
    // para mantener un codigo uniforme envolvemos ValidationResultDTO en un HttpErrorResponse
    if (this.isValidationResponse(errorObj)) {
      let err = error;
      if (error && error.status) {
        err = error.error
      }
      this.applyValidationResult(err, fieldGroupList, formGroup, model);
      return true;
    } else {
      console.error('Error al tratar error de validacion', error);
      return false;
    }
  }

  private applyValidationResult(
    validationResult: ValidationResultDTO,
    fieldGroups: FieldGroup[],
    formGroup: FormGroup,
    model: any
  ): void {
    // limpiar errores previos
    this.markFormGroupTouched(formGroup);
    fieldGroups.forEach((fieldGroup: FieldGroup) => {
      this.deleteModelErrors(new FormGroupConfig(fieldGroup, model));
    });
    // aplicar errores
    validationResult.errors.forEach((error: ValidationExecutionDTO) => {
      // buscar por jsonPath
      let formFieldList = this.getErrorFormControlsFromPath(
        error,
        fieldGroups,
        model
      );
      if (formFieldList.length === 0) {
        // si no se encuentra se busca por la definicion del formulario
        formFieldList = this.getErrorFormControlsFromValidation(
          error,
          fieldGroups,
          model
        );
      }
      if (formFieldList.length > 0) {
        formFieldList.forEach((formControl: AbstractControl) =>
          this.setFormControlError(formControl, error)
        );
      } else {
        // no se ha encontrado control de formulario erroneo por jsonPath, ni por definicion de los campos
        console.warn(
          'No se ha encontrado control de formulario erroneo por jsonPath, ni por definicion de los campos',
          error
        );
        this.setFormControlError(formGroup, error);
      }
    });
  }

  private getErrorFormControlsFromPath(
    error: any,
    fieldGroup: FieldGroup[],
    model: any
  ): Array<AbstractControl> {
    let errorControls: Array<AbstractControl>;
    let path = error.jsonPath;
    if (path) {
      errorControls = new Array<AbstractControl>();
      const fieldConfigList = this.getFieldConfigurations(fieldGroup);
      const tableExpMatch = path.match(TABLEPATH_REGEX);
      if (tableExpMatch) {
        const tablePath = tableExpMatch[1];
        const index = tableExpMatch[2];
        const rowObjectPath = tablePath + '[' + index + ']';
        const tableColPath = tableExpMatch[3];
        // asociar error a FormControl de la tablas
        const tableFieldConfigList = fieldConfigList.filter(config => config.jsonField === tablePath);
        if (tableFieldConfigList) {
          tableFieldConfigList.forEach(tableFieldConfig => {
            errorControls.push(tableFieldConfig.abstractControl);
          });
        }
        if (index > -1) {
          // asociar error a fila de la tabla
          // extraer rowUuid del elemento n-esimo indicado por el jsonPath
          let rowObjectPathArray = this.jsonPathToArray(rowObjectPath);
          let rowObj = objectPath.get(model, rowObjectPathArray);
          if (!rowObj) {
            // lo busco con pregijo dto.
            rowObjectPathArray = this.jsonPathToArray('dto.' + rowObjectPath);
            rowObj = objectPath.get(model, rowObjectPathArray);
          }
          if (rowObj) {
            // establecer error a la fila de la tabla en el modelo
            this.setObjectControlError(rowObj, error);
          }
          if (tableColPath) {
            // buscar FieldConfig asociado a path de la tabla
            if (tableFieldConfigList && tableFieldConfigList.length > 0) {
              // filtrar por la tabla que tiene asociado el campo concreto de la tabla
              tableFieldConfigList.forEach(tableFieldConfig => {
                const fields = this.getFieldConfigFromPath(tableFieldConfig.tableColumns, tableColPath);
                if (fields.length > 0) {
                  fields.forEach((colField: FieldConfig) => {
                    if (colField.abstractControl) {
                      errorControls.push(colField.abstractControl);
                    }
                  });
                }
              })
            }

          }
        }


        // es un error de una tabla en la que no se ha especificado campo, asociar a toda la tabla
        //          errorControls = this.getAbstractControlFromFieldConfig(fieldConfigList, );



      } else {
        errorControls = this.getAbstractControlFromFieldConfig(fieldConfigList, path);
      }
    } else {
      errorControls = new Array<AbstractControl>();
    }
    return errorControls;
  }

  private getFieldConfigurations(fieldGroup: FieldGroup[]): Array<FieldConfig> {
    let fields = new Array<FieldConfig>();
    [].concat(...fieldGroup.map((group: FieldGroup) => group.fieldList)).forEach((fieldConfig: FieldConfig) => {
      fields.push(fieldConfig);
    });
    return fields;
  }

  private getFieldConfigFromPath(fields: Array<FieldConfig>, path: string): Array<FieldConfig> {
    return fields.filter((field: FieldConfig) => path === field.jsonField);
  }

  private getAbstractControlFromFieldConfig(fields: Array<FieldConfig>, path: string): Array<AbstractControl> {
    let controls = this.getFieldConfigFromPath(fields, path)
      .filter((field: FieldConfig) => field.abstractControl)
      .map((field: FieldConfig) => field.abstractControl);
    if (controls?.length === 0 && !path.startsWith('dto.')) {
      // lo busco con pregijo dto.
      controls = this.getAbstractControlFromFieldConfig(fields, 'dto.' + path);
    }
    return controls;
  }

  private getErrorFormControlsFromValidation(
    error: any,
    fieldGroups: Array<FieldGroup>,
    model: any
  ): Array<AbstractControl> {
    let errorControls = new Array<AbstractControl>();
    fieldGroups.forEach((fieldGroup: FieldGroup) => {
      let invalidFields = fieldGroup.fieldList.filter(
        (fieldConfig: FieldConfig) => {
          let isInvalidField = this.isInvalidField(fieldConfig, error);
          if (fieldConfig.type === 'TABLE') {
            if (fieldConfig.tableColumns) {
              // comprobar si error afecta a alguna columna
              fieldConfig.tableColumns
                .filter((columnConfig: FieldConfig) =>
                  this.isInvalidField(columnConfig, error)
                )
                .forEach((columnConfig: FieldConfig) => {
                  // acceder por jsonPath a control con error
                  // 1. obtener del modelo registro apuntado por jsonPath del error
                  let rowObjectPathIndex = error.jsonPath.lastIndexOf(']');
                  if (rowObjectPathIndex > -1) {
                    let rowObjectPath = error.jsonPath.substring(
                      0,
                      rowObjectPathIndex + 1
                    );
                    let rowObjectPathArray = this.jsonPathToArray(
                      rowObjectPath
                    );
                    let rowObj = objectPath.get(model, rowObjectPathArray);
                    if (rowObj) {
                      // establecer error a la fila de la tabla en el modelo
                      this.setObjectControlError(rowObj, error);
                      // anyadir error a la tabla contenedora
                      let formControl = fieldGroup.formGroup.get(
                        this.getFormControlKey(fieldConfig)
                      );
                      if (formControl) {
                        errorControls.push(formControl);
                      }
                      // let rowuuid = rowObj['rowUuid'];
                      // if (rowuuid) {
                      //   let errorFielKey = this.getFormControlKey(columnConfig, rowuuid);
                      //   // 2. Obtener FormControl con error ¿Opcion 2?
                      //   let tableControl = fieldConfig.abstractControl.get(errorFielKey);
                      //   if (tableControl) {
                      //     errorControls.push(tableControl);
                      //   }
                      // }
                    }
                  } else {
                    let errorFielKey = this.getFormControlKey(columnConfig);
                    let control = fieldConfig.abstractControl.get(errorFielKey);
                    if (control) {
                      errorControls.push(control);
                    }
                  }
                });
            }
          }
          return isInvalidField;
        }
      );
      if (invalidFields) {
        invalidFields.forEach((fieldConfig: FieldConfig) => {
          errorControls.push(
            fieldGroup.formGroup.get(this.getFormControlKey(fieldConfig))
          );
        });
      }
    });
    return errorControls;
  }

  /**
   * Transforma un path JSON en string en un array de propiedades para su uso por
   * la libreria de objectPath
   */
  private jsonPathToArray(path: string): Array<string> {
    console.log('string path ', path);
    let pathArray = [];
    if (path) {
      let rowObjectPath = path.replace(/\[|\]/g, '.');
      console.log('row path ', rowObjectPath);
      if (rowObjectPath.endsWith('.')) {
        rowObjectPath = rowObjectPath.substring(0, rowObjectPath.length - 1);
      }
      console.log('row path modificado', rowObjectPath);
      pathArray = rowObjectPath.split('.');
    }
    console.log('Array path ', path);
    return pathArray;
  }

  public setFormControlError(
    formField: AbstractControl,
    error: ValidationExecutionDTO
  ) {
    this.setObjectControlError(formField, error);
  }

  private setObjectControlError(object: any, error: ValidationExecutionDTO) {
    if (object) {
      console.log('Setting errors', object, error);
      let errors = object.errors || [];
      let serverErrors = errors.server;
      let addError = error != undefined;
      if (!serverErrors) {
        serverErrors = new Array<ValidationExecutionDTO>();
        errors.server = serverErrors;
      } else {
        let sameErrorExists: Array<
          ValidationExecutionDTO
        > = serverErrors.filter(
          (prevError: ValidationExecutionDTO) =>
            prevError.message === error.message
        );
        addError = !sameErrorExists.length;
      }
      if (addError) {
        serverErrors.push(error);
      }
      if (object instanceof AbstractControl) {
        // eliminar error y validador anterior
        this.clearFormControlError(object);
        if (error) {
          object.setErrors(errors);
          object['tmpServerValidators'] = this.serverMsgValidator(errors);
          object.addValidators(object['tmpServerValidators']);
        }
        console.log(object.hasValidator(object['tmpServerValidators']));
      } else {
        object.errors = errors;
      }
    } else {
      console.log(error.validationCode, error.message, error.result);
    }
  }
  private serverMsgValidator(errors: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return errors;
    };
  }



  private isInvalidField(
    fieldConfig: FieldConfig,
    error: ValidationExecutionDTO
  ) {
    return (
      fieldConfig.validations &&
      ((typeof fieldConfig.validations === 'string' &&
        fieldConfig.validations === error.validationCode) ||
        fieldConfig.validations.indexOf(error.validationCode) > -1)
    );
  }

  public isValidationResponse(error: any): boolean {
    // a este metodo se puede llegar como resultado de un error HttpErrorResponse,
    // o como resultado de una peticion HTTP de validacion  ValidationResultDTO
    // para mantener un codigo uniforme envolvemos ValidationResultDTO en un HttpErrorResponse
    let err = error;
    if (error && error.status) {
      err = error.error
    }
    return err && err.errors && err.result;
  }

  /**
   * Método de utilidad a usar para actualizar el modelo en el que se respalda un componente
   *
   * @param model modelo de la pantalla o componente
   * @param updatedModel datos actualiados del modelo. Este objeto debe ser del mismo tipo que el de model
   */
  public merge(model: any, updatedModel: any): void {
    // lodash merge no borra propiedades de model que no esten en updatedModel
    this.removeAbsentProperties(model, updatedModel);
    lodash.mergeWith(model, updatedModel, (modelValue, updatedModelValue, propertyName, modelObject, updatedModelObject) => {
      let finalValue;
      console.log(propertyName)
      if (this.isInternalProperty(propertyName)) {
        // no modificamos valor de propiedades internas del formulario
        finalValue = modelValue
      } else if (lodash.isArray(modelValue) && updatedModelValue) {
        // dejar array de model con la misma longitud para que se haga merge con el mismo numero de objetos en la lista
        modelValue.splice(updatedModelValue.length);
      } else if (!lodash.isObject(modelValue)) {
        // devolver misma referencia al objeto model para no afectar asociación de formularios a objetos
        // el merge se hace solo del valor las propiedades "hoja"
        finalValue = updatedModelValue;
      }
      return finalValue;
    });
  }

  private removeAbsentProperties(obj, src): void {
    lodash.forOwn(obj, (value, key) => {
      if (!this.isInternalProperty(key)) {
        if (lodash.isObject(value)) {
          if (!lodash.has(src, key)) {
            delete obj[key];
          } else {
            this.removeAbsentProperties(value, src[key]);
          }
        } else if (!lodash.has(src, key)) {
          delete obj[key];
        }
      }
    });
  }

  private isInternalProperty(propertyName: string) {
    return propertyName === '_parent' || propertyName === 'rowUuid';
  }

  public cleanObjectProperties(object: any): any {
    return JSON.stringify(object, (key, value) => this.isInternalProperty(key) ? undefined : value);
  }

  public getDescriptionCol(formElement: formElementComponent, descriptionObj: I18nDescribable | any): string {
    return this.getEvalDescription(formElement, descriptionObj, formElement);
  }
  public getGroupDescription(evaluableExecutor: IEvaluableExecutor, descriptionObj: I18nDescribable): string {
    return this.getEvalDescription(evaluableExecutor, descriptionObj, this);
  }
  public getDescription(descriptionObj: I18nDescribable, _this: any): string {
    return this.getEvalDescription(null, descriptionObj, _this);
  }

  public isVisible(formGroupConfig: FormGroupConfig, _this: any): boolean {
    let visible = true;
    if (formGroupConfig.visibility) {
      visible = this.evalExpression(formGroupConfig.visibility,
        [formGroupConfig.model],
        formGroupConfig,
        _this,
        formGroupConfig.functions);
    }
    return visible;
  }

  public getEvalDescription(evaluableExecutor: IEvaluableExecutor, descriptionObj: I18nDescribable, _this: any): string {
    let desc: string;
    if (descriptionObj) {
      if (evaluableExecutor && descriptionObj.descriptionKeyExpression) {
        try {
          desc = this.evalExpression(descriptionObj.descriptionKeyExpression,
            [evaluableExecutor.model], evaluableExecutor, _this, evaluableExecutor.functions);
          if (desc) {
            const cacheKey = descriptionObj.descriptionKeyExpression;
            if (this.evalDescriptionCache.has(cacheKey)) {
              return this.evalDescriptionCache.get(cacheKey)!;
            }
            desc = desc + '';
            desc = this.translationService.instant(desc) || desc;
            this.evalDescriptionCache.clear();
            this.evalDescriptionCache.set(cacheKey, desc);
          }
        } catch (error: any) {
          console.error('Error al evaluar descripcion de ', evaluableExecutor);
        }
      } else if (descriptionObj.descriptionKey) {
        desc = this.translationService.instant(
          descriptionObj.descriptionKey
        ) || descriptionObj.descriptionKey;
      } else if (descriptionObj.description) {
        desc = descriptionObj.description
        if (!(typeof desc === 'string')) {
          desc = desc[this.translationService.currentLang] || desc;
        }
      } else if (typeof descriptionObj === 'string') {
        desc = descriptionObj;
      }
    }
    return desc;
  }

  public evalExpression(expression: string, args: any[], objToApply: any, _this: any, functions?: Array<FunctionDef>): any {
    // let expressionArgs = (args) ? args : [this.model];
    // let objModel = (obj) ? obj : this;
    let tmpFunc = new Function('return ' + this.processExpression(expression, args, functions, _this));
    let value = tmpFunc.apply(objToApply, args);
    return value;
  }

  private processExpression(expression: string, args: any[], functions: Array<FunctionDef>, _this: any) {
    if (typeof expression !== 'string') {
      // la expresion puede ser un booleano
      return expression;
    }
    let _expression = this.processExpression$(expression);
    let cont = 0;
    while (_expression.indexOf('$functions.') != -1 && cont < 6) {
      cont++;
      let inicioFunction = _expression.indexOf('$functions.');
      // let evalInicio = expression.substring(0, inicioFunction).trim();
      let expressionNew = _expression.substring(
        inicioFunction + '$functions.'.length,
        _expression.length
      );

      let finFunction =
        expressionNew.indexOf(' ') != -1
          ? expressionNew.indexOf(' ')
          : expressionNew.length;
      let evalFunction = expressionNew.substring(0, finFunction).trim();
      // Buscamos parametros
      let evalFunctionWithParams = evalFunction;
      if (evalFunction.indexOf('(') != -1 && evalFunction.indexOf('(') != -1) {
        let params = evalFunction.substring(evalFunction.indexOf('(') + 1, evalFunction.lastIndexOf(')'));
        evalFunction = evalFunction.substring(0, evalFunction.indexOf('('));
        params.split(',').forEach(param => {
          args.push(eval(param));
        });
      }
      if (functions) {
        let func = functions.find(x => x.identifier === evalFunction);
        let resp = '';
        if (func) {
          resp = func.fn.apply(_this, args);
        } else {
          resp = 'true';
        }
        _expression = _expression.replace('$functions.' + evalFunctionWithParams, JSON.stringify(resp));
      }
    }
    return _expression;
  }

  private processExpression$(expression: string) {
    let _exp: string = expression.replace(
      new RegExp('\\$model', 'g'),
      'this.model'
    );
    _exp = _exp.replace(new RegExp('\\$context', 'g'), 'this.model.context');
    _exp = _exp.replace(
      new RegExp('\\$configurations', 'g'),
      'this.model.context.configurations'
    );
    return _exp;
  }


  // Metodos de adaptacion de formularios

  /**
   * Metodo que permite modificar una configuracion de formularios por parte de la adaptacion.
   * Se podra modificar lo siguiente
   * 1. Configuración de la pagina (objeto InterfaceSettings)
   * 2. Cambiar la configuración de los grupos de formularios que componen la pagina (objeto FieldGroup)
   *    2.1 Borrar un grupo existente: indicando el identificador de grupo y la operación DELETE
   *
   *          {
   *           "display": "accordion",
   *           ...
   *           "fieldGroups": [
   *             {
   *              "id": "grupo1",
   *               "adaptConfig": {
   *                  "operation": "DELETE"
   *                }
   *             }]
   *         }
   *
   *      2.2 Anyadir un nuevo grupo: indicando todas las propiedades del grupo ademas de la operacion INSERT, y alguna de
   *          las propiedades afterGroup o beforeGroup que indican si el nuevo grupo se posiciona antes o después de otro grupo
   *          existente en la configuracion de producto.
   *
   *          {
   *           "display": "accordion",
   *           ...
   *           "fieldGroups": [
   *             {
   *              "id": "grupo3",
   *               "adaptConfig": {
   *                  "operation": "INSERT",
   *                  "after": "grupo2"
   *                },
   *               "fieldList": [ ... ]
   *             }]
   *         }
   *
   *      2.3 Modificar un grupo de formulario
   *        2.3.1 Cambiar cualquier propiedad del grupo: descripción, editable, requerido, etc
   *        2.3.2 Cambiar la configuración de campos del grupo (objetos Interface FieldConfig y FieldConfigAdapt)
   *          2.3.2.1 Borrar un campo existente: Indicando el campo a eliminar (ya sea mediante el campo jsonField o el campo id),
   *                 y la operación DELETE
   *          2.3.2.2 Añadir un nuevo campo: Indicando toda la configuración del campo, la operación INSERT
   *                 y alguna de las propiedades afterField o beforeField que indican si el nuevo campo se
   *                  posiciona antes o después de otro campo existente en la configuración de producto.
   *          2.3.2.3 Modificar un campo existente: Indicando el campo a modificar (ya sea mediante el campo jsonField o el campo id),
   *                   y la operación UPDATE
   *
   *          {
   *           "display": "accordion",
   *           ...
   *           "fieldGroups": [
   *             {
   *              "id": "grupo2",
   *               "adaptConfig": {
   *                  "operation": "UPDATE"
   *                },
   *               "fieldList": [
   *                   {
   *                       "jsonField": "grupo2.field1",
   *                       "adaptConfig": {
   *                         "operation": "DELETE"
   *                       }
   *                     },
   *                     {
   *                       "descriptionKey": "grupo2.field2.descriptionKey modificada",
   *                       "jsonField": "grupo2.field2",
   *                       "adaptConfig": {
   *                         "operation": "UPDATE"
   *                       }
   *                     },
   *                     {
   *                       "id": "grupo2.field5.id",
   *                       "descriptionKey": "grupo2.field5.descriptionKey",
   *                       "jsonField": "grupo2.field5",
   *                       "type": "TEXT",
   *                       "required": true,
   *                       "editable": true,
   *                       "fieldStyle": "col-6",
   *                       "adaptConfig": {
   *                         "operation": "INSERT",
   *                         "before": "grupo2.field2"
   *                       }
   *                     }
   *                ]
   *             }]
   *         }
   */
  public mergeInterfaceSettings(
    settings: InterfaceSettings,
    adaptSettings: InterfaceSettings,
    preserveAdaptConfig = false
  ): InterfaceSettings {
    let result: InterfaceSettings;
    if (!lodash.isEmpty(adaptSettings)) {
      const groupsAdapt = adaptSettings.fieldGroups;
      result = lodash.cloneDeep(settings);
      // actualizar datos del objeto InterfaceSettings
      // eliminamos datos que no son de la clase InterfaceSettings antes del merge
      adaptSettings.fieldGroups = undefined;
      lodash.merge(result, adaptSettings);
      adaptSettings.fieldGroups = groupsAdapt;
      if (groupsAdapt) {
        groupsAdapt.forEach((fgAdapt: FieldGroup) => {
          if (fgAdapt.adaptConfig && fgAdapt.adaptConfig.operation) {
            switch (fgAdapt.adaptConfig.operation) {
              case AdaptOperation.UPDATE: {
                const fg = this.getFieldGroup(result, fgAdapt.id);
                if (fg) {
                  this.processGroupUpdate(fg, fgAdapt, preserveAdaptConfig);
                }
                break;
              }
              case AdaptOperation.INSERT: {
                this.processGroupInsert(result.fieldGroups, fgAdapt, preserveAdaptConfig);
                break;
              }
              case AdaptOperation.DELETE: {
                result.fieldGroups = result.fieldGroups.filter(
                  (fg: FieldGroup) => fg.id !== fgAdapt.id
                );
                break;
              }
            }
          }
        });
      }
    } else {
      result = settings;
    }
    return result;
  }

  // Inicio adaptaciones de formularios
  private processGroupInsert(
    fieldGroups: FieldGroup[],
    fgAdapt: FieldGroup,
    preserveAdaptConfig: boolean
  ): void {
    // buscar posicion del grupo
    let groupIndex = -1;
    let fieldReference = fgAdapt.adaptConfig.before;
    if (fieldReference) {
      groupIndex = fieldGroups.findIndex(
        (fg: FieldGroup) => fieldReference === fg.id
      );
    }
    fieldReference = fgAdapt.adaptConfig.after;
    if (groupIndex === -1 && fieldReference) {
      groupIndex = fieldGroups.findIndex(
        (fg: FieldGroup) => fieldReference === fg.id
      );
      if (groupIndex > -1) {
        groupIndex++;
      }
    }
    if (groupIndex === -1) {
      groupIndex = fieldGroups.length;
    }
    // const newGroup = lodash.cloneDeep(fgAdapt);
    fieldGroups.splice(groupIndex, 0, fgAdapt);
  }

  private processGroupUpdate(fg: FieldGroup, fgAdapt: FieldGroup, preserveAdaptConfig: boolean): void {
    // almacenar propiedades FieldGroupAdapt antes del merge
    const fieldGroupAdaptProperties = this.prepareGroupToClone(fgAdapt, true);
    lodash.merge(fg, fgAdapt);
    // reestablecer propiedades FieldGroupAdapt
    this.reverPrepareGroupToCloneChanges(fgAdapt, fieldGroupAdaptProperties);
    // tratar adaptaciones campos del grupo
    const fieldListAdapt = fgAdapt.fieldList;
    if (fieldListAdapt) {
      fieldListAdapt.forEach((fcAdapt: FieldConfig) => {
        if (fcAdapt.adaptConfig && fcAdapt.adaptConfig.operation) {
          switch (fcAdapt.adaptConfig.operation) {
            case AdaptOperation.UPDATE: {
              const fc = this.getField(fg, this.getFieldId(fcAdapt));
              if (fc) {
                this.processFieldUpdate(fc, fcAdapt, preserveAdaptConfig);
              }
              break;
            }
            case AdaptOperation.INSERT: {
              this.processFieldInsert(fg, fcAdapt, preserveAdaptConfig);
              break;
            }
            case AdaptOperation.DELETE: {
              fg.fieldList = fg.fieldList.filter(
                (fc: FieldConfig) =>
                  this.getFieldId(fc) !== this.getFieldId(fcAdapt)
              );
              break;
            }
          }
        }
      });
    }
  }
  private processFieldUpdate(fc: FieldConfig, fcAdapt: FieldConfig, preserveAdaptConfig: boolean): void {
    let adaptProperties;
    if (!preserveAdaptConfig) {
      // eliminar propiedades FieldGroupAdapt antes merge
      adaptProperties = this.prepareToClone(fcAdapt);
    }
    lodash.merge(fc, fcAdapt);
    if (!preserveAdaptConfig) {
      // restablecer propiedades FieldGroupAdapt
      this.reverPrepareToCloneChanges(fcAdapt, adaptProperties);
    }
  }

  private processFieldInsert(
    fieldGroup: FieldGroup,
    fcAdapt: FieldConfig,
    preserveAdaptConfig: boolean
  ): void {
    let productFieldList = fieldGroup.fieldList;
    // buscar posicion del campo
    let fieldIndex = -1;
    let fieldReference = fcAdapt.adaptConfig.before;
    if (fieldReference) {
      fieldIndex = productFieldList.findIndex(
        (fc: FieldConfig) => fieldReference === this.getFieldId(fc)
      );
    }
    fieldReference = fcAdapt.adaptConfig.after;
    if (fieldIndex === -1 && fieldReference) {
      fieldIndex = productFieldList.findIndex(
        (fc: FieldConfig) => fieldReference === this.getFieldId(fc)
      );
      if (fieldIndex > -1) {
        fieldIndex++;
      }
    }
    if (fieldIndex === -1) {
      fieldIndex = productFieldList.length;
    }
    // eliminar propiedades FieldGroupAdapt antes clone
    let adaptProperties;
    if (!preserveAdaptConfig) {
      adaptProperties = this.prepareToClone(fcAdapt);
    }
    const newField = lodash.cloneDeep(fcAdapt);
    if (!preserveAdaptConfig) {
      // reestablecer propiedades FieldGroupAdapt
      this.reverPrepareToCloneChanges(fcAdapt, adaptProperties);
    }
    productFieldList.splice(fieldIndex, 0, newField);
  }

  /**
   * Devuelve el grupo que contiene el identificador indicado
   *
   * @param object
   * @param identifier
   */
  private getFieldGroup(
    object: InterfaceSettings,
    identifier: any
  ): FieldGroup {
    let fg: FieldGroup;
    const groups = object.fieldGroups;
    if (groups) {
      fg = groups.find((group: FieldGroup) => group.id === identifier);
    }
    return fg;
  }

  /**
   * Devuelve el campo de un grupo que contiene el identificador indicado
   *
   * @param fg: Agrupacion de campos en los que buscar
   * @param identifier: identificador a buscar
   */
  private getField(fg: FieldGroup, identifier: string): FieldConfig {
    let fc: FieldConfig;
    if (fg.fieldList) {
      fc = fg.fieldList.find(
        (field: FieldConfig) => this.getFieldId(field) === identifier
      );
    }
    return fc;
  }

  private getFieldId(fc: FieldConfig): string {
    return fc.id || fc.jsonField;
  }

  /**
   * Establece a nulo las propiedades extendidas del objeto FieldConfigAdapt a nulo
   * para que se pueda copiar en un objeto de tipo FieldConfig.
   * Devuelve un objeto de tipo FieldConfigAdapt que podrá se utilizado para reestablecer
   * las propiedades iniciales del objeto de adaptación mediante  reverClonePreparationChanges
   */
  private prepareToClone(fc: FieldConfig): FieldConfig {
    const fieldAdaptProperties = new FieldConfig();
    fieldAdaptProperties.adaptConfig = fc.adaptConfig;
    fc.adaptConfig = undefined;
    return fieldAdaptProperties;
  }

  /**
   * Revierte los cambios realizados en un objeto FieldConfigAdapt del proceso de limpia
   * del metodo prepareToClone.
   *
   * @param fc: objeto a reestablecer
   * @param adaptProperties: objeto resultante de la llamada a prepareToClone
   */
  private reverPrepareToCloneChanges(
    fc: FieldConfig,
    adaptProperties: FieldConfig
  ): void {
    lodash.merge(fc, adaptProperties);
  }

  private prepareGroupToClone(
    fg: FieldGroup,
    deleteFieldList: boolean
  ): FieldGroup {
    const groupAdaptProperties = new FieldGroup();
    groupAdaptProperties.adaptConfig = fg.adaptConfig;
    if (deleteFieldList) {
      groupAdaptProperties.fieldList = fg.fieldList;
    }
    fg.adaptConfig = undefined;
    if (deleteFieldList) {
      fg.fieldList = undefined;
    }
    return groupAdaptProperties;
  }

  private reverPrepareGroupToCloneChanges(
    fg: FieldGroup,
    adaptProperties: FieldGroup
  ): void {
    lodash.merge(fg, adaptProperties);
  }

  public navigateToGroup(fieldGroup: FieldGroup) {
    this.setGroupActive(fieldGroup);
    let timeToNavigate = (fieldGroup.active) ? 0 : 1000;
    timer(timeToNavigate).pipe(take(1)).subscribe(() => {
      this.scrollToElement(document.querySelector('#' + this.getFormGroupId(fieldGroup.id)));
    });
  }

  public navigateToField(field: FieldConfig, scrollTo = true) {
    let elementId = field.id;
    this.setGroupActive(field.fieldGroup);
    let timeToNavigate = (field.fieldGroup.active) ? 0 : 1000;
    timer(timeToNavigate).pipe(take(1)).subscribe(() => {
      console.log('navigate to ', elementId);
      let input = document.querySelector('#' + this.formatControlId(elementId));
      let inputWrap = document.querySelector('#' + this.formatControlId(elementId) + 'ui');
      let inputBtn = document.querySelector('#' + this.formatControlId(elementId) + 'btn');
      console.log('navigate to input ', input);
      if (input) {
        let obj: any = input;
        if (obj.focus) {
          obj.focus();

        }
      }
      if (inputWrap) {
        let obj: any = inputWrap;
        if (obj.focus) {
          obj.focus();

        }
      }
      if (inputBtn) {
        let obj: any = inputBtn;
        if (obj.focus) {
          obj.focus();

        }
      }
      if (scrollTo) {
        this.scrollToElement(document.querySelector('#' + elementId + '_label') || input);
      }
    });
  }


  private setGroupActive(fieldGroup: FieldGroup): void {
    if (fieldGroup) {
      // eliminar marca activo del resto de grupos en pestañas
      if (fieldGroup.interfaceSettings && fieldGroup.interfaceSettings.display === InterfaceDisplayType.TAB) {
        fieldGroup.interfaceSettings.fieldGroups.filter((group: FieldGroup) => group.id !== fieldGroup.id)
          .forEach((group: FieldGroup) => group.active = false);
      }
      // marcar como activo del resto de grupos
      fieldGroup.active = true;
    }
  }

  private scrollToElement(htmlElement: any): void {
    console.log('scroll to ', htmlElement);
    if (htmlElement && htmlElement.scrollIntoView) {
      // scroll to your element
      htmlElement.scrollIntoView(true);
      let viewportOffset = htmlElement.getBoundingClientRect();
      // these are relative to the viewport, i.e. the window
      let top = viewportOffset.top;
      let scrolledYWin = window.scrollY;
      if (top && scrolledYWin && top < 220) {
        window.scroll(0, scrolledYWin + top - 220);
      }
    }
  }
}
